import { useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "../services/apiClient";
import { createBrokerDashboardApi } from "../services/brokerDashboardApi";
import { logError } from "../utils/logger";

const brokerActionApi = createBrokerDashboardApi();

// Fetch helpers using apiClient directly (mirrors dashboardApi broker methods)
const fetchBrokerProfileReq = () => api.get("/brokers/me/profile");
const fetchManagedPropertiesReq = () => api.get("/brokers/managed-properties");
const fetchMarketplacePropertiesReq = () => api.get("/brokers/marketplace");
const fetchCommissionsReq = () => api.get("/brokers/commissions");
const fetchBrokerLeadsReq = () => api.get("/brokers/leads");
const fetchLeadsFunnelReq = () => api.get("/analytics/leads-funnel");
const fetchRepresentedDevelopmentsReq = () =>
  api.get("/broker-units/my-developments");

export const useBrokerDashboard = () => {
  const [brokerProfile, setBrokerProfile] = useState(null);
  const [managedProperties, setManagedProperties] = useState([]);
  const [marketplaceProperties, setMarketplaceProperties] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [representedDevelopments, setRepresentedDevelopments] = useState([]);
  const [brokerLeads, setBrokerLeads] = useState([]);
  const [leadsFunnel, setLeadsFunnel] = useState(null);

  const [showCommissionModal, setShowCommissionModal] = useState(false);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [newCommission, setNewCommission] = useState({
    property_id: "",
    property_title: "",
    sale_amount: "",
    commission_percentage: 3,
    currency: "MXN",
    closing_date: "",
    notes: "",
  });
  const [newLead, setNewLead] = useState({
    client_name: "",
    client_phone: "",
    client_email: "",
    property_id: "",
    notes: "",
    source: "directo",
  });

  // ---- Fetches ----
  const fetchBrokerProfile = useCallback(async () => {
    try {
      const r = await fetchBrokerProfileReq();
      if (r.ok) setBrokerProfile(await r.json());
    } catch (e) {
      logError("useBrokerDashboard.fetchBrokerProfile", e);
    }
  }, []);

  const fetchManagedProperties = useCallback(async () => {
    try {
      const r = await fetchManagedPropertiesReq();
      if (r.ok) setManagedProperties(await r.json());
    } catch (e) {
      logError("useBrokerDashboard.fetchManagedProperties", e);
    }
  }, []);

  const fetchMarketplaceProperties = useCallback(async () => {
    try {
      const r = await fetchMarketplacePropertiesReq();
      if (r.ok) {
        const d = await r.json();
        setMarketplaceProperties(d.properties || []);
      }
    } catch (e) {
      logError("useBrokerDashboard.fetchMarketplaceProperties", e);
    }
  }, []);

  const fetchCommissions = useCallback(async () => {
    try {
      const r = await fetchCommissionsReq();
      if (r.ok) setCommissions(await r.json());
    } catch (e) {
      logError("useBrokerDashboard.fetchCommissions", e);
    }
  }, []);

  const fetchBrokerLeads = useCallback(async () => {
    try {
      const r = await fetchBrokerLeadsReq();
      if (r.ok) setBrokerLeads(await r.json());
    } catch (e) {
      logError("useBrokerDashboard.fetchBrokerLeads", e);
    }
  }, []);

  const fetchLeadsFunnel = useCallback(async () => {
    try {
      const r = await fetchLeadsFunnelReq();
      if (r.ok) setLeadsFunnel(await r.json());
    } catch (e) {
      logError("useBrokerDashboard.fetchLeadsFunnel", e);
    }
  }, []);

  const fetchRepresentedDevelopments = useCallback(async () => {
    try {
      const r = await fetchRepresentedDevelopmentsReq();
      if (r.ok) setRepresentedDevelopments(await r.json());
    } catch (e) {
      logError("useBrokerDashboard.fetchRepresentedDevelopments", e);
    }
  }, []);

  const fetchAll = useCallback(async () => {
    await Promise.all([
      fetchBrokerProfile(),
      fetchManagedProperties(),
      fetchMarketplaceProperties(),
      fetchCommissions(),
      fetchBrokerLeads(),
      fetchRepresentedDevelopments(),
      fetchLeadsFunnel(),
    ]);
  }, [
    fetchBrokerProfile,
    fetchManagedProperties,
    fetchMarketplaceProperties,
    fetchCommissions,
    fetchBrokerLeads,
    fetchRepresentedDevelopments,
    fetchLeadsFunnel,
  ]);

  // ---- Actions ----
  const createLead = useCallback(async () => {
    if (!newLead.client_name?.trim()) {
      toast.error("Nombre requerido");
      return;
    }
    try {
      const r = await brokerActionApi.createLead({
        ...newLead,
        property_id: newLead.property_id || null,
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || "Error");
      }
      toast.success("Lead registrado");
      setShowLeadModal(false);
      setNewLead({
        client_name: "",
        client_phone: "",
        client_email: "",
        property_id: "",
        notes: "",
        source: "directo",
      });
      fetchBrokerLeads();
    } catch (e) {
      toast.error(e.message);
    }
  }, [newLead, fetchBrokerLeads]);

  const updateLeadStatus = useCallback(
    async (leadId, status) => {
      try {
        const r = await brokerActionApi.updateLeadStatus(leadId, status);
        if (r.ok) {
          toast.success("Estado actualizado");
          fetchBrokerLeads();
        }
      } catch (e) {
        toast.error("Error");
      }
    },
    [fetchBrokerLeads],
  );

  const notifyQualifiedLead = useCallback(
    async (leadId) => {
      try {
        const r = await brokerActionApi.notifyQualifiedLead(leadId);
        if (r.ok) {
          toast.success("Propietario notificado");
          fetchBrokerLeads();
        } else {
          const e = await r.json();
          toast.error(e.detail || "Error");
        }
      } catch (e) {
        toast.error("Error");
      }
    },
    [fetchBrokerLeads],
  );

  const createCommission = useCallback(async () => {
    try {
      const r = await brokerActionApi.createCommission({
        ...newCommission,
        sale_amount: parseFloat(newCommission.sale_amount),
        commission_percentage: parseFloat(newCommission.commission_percentage),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || "Error al registrar comisión");
      }
      toast.success("Comisión registrada");
      setShowCommissionModal(false);
      setNewCommission({
        property_id: "",
        property_title: "",
        sale_amount: "",
        commission_percentage: 3,
        currency: "MXN",
        closing_date: "",
        notes: "",
      });
      fetchCommissions();
    } catch (e) {
      toast.error("Error");
    }
  }, [newCommission, fetchCommissions]);

  const updateCommissionStatus = useCallback(
    async (id, status) => {
      try {
        const r = await brokerActionApi.updateCommissionStatus(id, status);
        if (r.ok) {
          toast.success("Estado actualizado");
          fetchCommissions();
        }
      } catch (e) {
        toast.error("Error");
      }
    },
    [fetchCommissions],
  );

  const deleteCommission = useCallback(
    async (id) => {
      if (!window.confirm("¿Eliminar esta comisión?")) return;
      try {
        const r = await brokerActionApi.deleteCommission(id);
        if (r.ok) {
          toast.success("Comisión eliminada");
          fetchCommissions();
        }
      } catch (e) {
        toast.error("Error");
      }
    },
    [fetchCommissions],
  );

  const reserveUnit = useCallback(
    async (unitId, leadId = null) => {
      try {
        const r = await brokerActionApi.reserveUnit(unitId, leadId);
        if (r.ok) {
          toast.success("Unidad apartada");
          fetchRepresentedDevelopments();
        } else {
          const e = await r.json();
          toast.error(e.detail || "Error");
        }
      } catch (e) {
        toast.error("Error");
      }
    },
    [fetchRepresentedDevelopments],
  );

  const releaseUnit = useCallback(
    async (unitId) => {
      try {
        const r = await brokerActionApi.releaseUnit(unitId);
        if (r.ok) {
          toast.success("Unidad liberada");
          fetchRepresentedDevelopments();
        } else {
          const e = await r.json();
          toast.error(e.detail || "Error");
        }
      } catch (e) {
        toast.error("Error");
      }
    },
    [fetchRepresentedDevelopments],
  );

  return {
    // State
    brokerProfile,
    managedProperties,
    marketplaceProperties,
    commissions,
    representedDevelopments,
    brokerLeads,
    leadsFunnel,
    showCommissionModal,
    setShowCommissionModal,
    showLeadModal,
    setShowLeadModal,
    newCommission,
    setNewCommission,
    newLead,
    setNewLead,
    // Fetches
    fetchAll,
    fetchBrokerLeads,
    fetchCommissions,
    fetchRepresentedDevelopments,
    // Actions
    createLead,
    updateLeadStatus,
    notifyQualifiedLead,
    createCommission,
    updateCommissionStatus,
    deleteCommission,
    reserveUnit,
    releaseUnit,
  };
};
